import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';

/**
 * Collection tabs component.
 *
 * Swaps between server-rendered product-grid panels by toggling the `hidden`
 * attribute. Every panel ships in the DOM, so switching is instant and needs no
 * fetch. Without JS the first panel shows — graceful progressive enhancement.
 *
 * Automatic activation (arrow keys move focus *and* select) is used because
 * switching is cheap.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class CollectionTabs extends Component {
  requiredRefs = ['tablist'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.tablist.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.tablist.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @returns {HTMLButtonElement[]} */
  get tabs() {
    return this.refs.tabs ?? [];
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return this.refs.panels ?? [];
  }

  /** @returns {number} */
  get selectedIndex() {
    const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return index === -1 ? 0 : index;
  }

  /**
   * Activate a tab and reveal its panel.
   *
   * @param {number|string} index
   * @param {PointerEvent|KeyboardEvent} [event]
   */
  select(index, event) {
    const { tabs, panels } = this;
    if (tabs.length === 0) return;

    const target = clamp(Number(index), 0, tabs.length - 1);

    for (const [i, tab] of tabs.entries()) {
      const active = i === target;
      tab.setAttribute('aria-selected', active.toString());
      tab.tabIndex = active ? 0 : -1;
      panels[i]?.toggleAttribute('hidden', !active);
    }

    const tab = tabs[target];
    if (!tab) return;

    // Only pull focus for keyboard activation; a click already focuses the tab.
    if (event instanceof KeyboardEvent) tab.focus();
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    this.#refreshCarousel(panels[target]);
  }

  /**
   * Roving-focus keyboard navigation (Arrow keys, Home, End) with wrap-around.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const count = this.tabs.length;
    if (count === 0) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = this.selectedIndex + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = this.selectedIndex - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select((next + count) % count, event);
  };

  /**
   * A carousel measured while its panel was `hidden` reports a zero width. Nudge
   * it to recalculate the first time its panel is revealed.
   *
   * @param {HTMLElement} [panel]
   */
  #refreshCarousel(panel) {
    if (!panel || !panel.hasAttribute('data-has-carousel') || panel.dataset.carouselReady) return;
    panel.dataset.carouselReady = 'true';
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
